/**
 * The admin portal's menus for the shared panel shell (redesign, admin 0.53.0): one ShellNav per
 * platform built from the page registries the portal already has (retailPages, loansPages, the Hub's
 * capability actions, the Shared lists), permission-gated exactly as before:
 *   Retail  — pagesFor(roles), the ROP roles each domain shell shows a page to;
 *   Loans   — loansPagesFor(actions), the pcb-loans-api capability answer;
 *   Hub     — the Hub capability answer (an action with a portal page lists that page);
 *   Shared  — sharedItemsFor(roles), shown while Retail is ready (as the old header did).
 * Plus the common tasks per platform and everything the search can find.
 */

import { useMemo } from 'react';
import { pagesToSearchItems, tasksToSearchItems, type SearchItem, type ShellNav, type ShellPage, type ShellPlatform, type ShellTask } from '@retail-ops/panel-shell';
import { useAuth } from '@/contexts/AuthContext';
import { usePlatforms } from '@/contexts/PlatformsContext';
import { SIX_SECTIONS, type Capabilities, type PlatformStatus } from '@/platforms/types';
import { pagesFor, RETAIL_BASE } from '@/platforms/retail/retailPages';
import { LOANS_BASE, loansPagesFor } from '@/platforms/loans/loansPages';
import { LOANS_GUIDE, LOANS_HOME_HELP, LOANS_TASKS } from '@/platforms/loans/loansGuide';
import { sharedItemsFor } from '@/platforms/shared/SharedArea';

export type AdminPlatformId = 'retail' | 'loans' | 'hub' | 'shared';

// ─── Retail ──────────────────────────────────────────────────────────────

export const RETAIL_HOME_HELP =
  'Retail Operations configuration: products and rates, settings, compliance rules, notifications, documents and who has access. Every change asks for a reason and is recorded in the change history, where it can be restored.';

export function retailNav(roles: ReadonlyArray<string>): ShellNav {
  const pages = pagesFor(roles);
  return {
    root: { label: 'Retail', path: RETAIL_BASE },
    homeHelp: RETAIL_HOME_HELP,
    sections: SIX_SECTIONS.map((s) => ({
      id: s.id,
      label: s.label,
      path: `${RETAIL_BASE}/${s.id}`,
      holds: s.holds,
      pages: pages
        .filter((pg) => pg.section === s.id)
        .map<ShellPage>((pg) => ({ path: pg.path, label: pg.label, section: pg.section, group: pg.group, help: pg.help, synonyms: pg.synonyms })),
    })).filter((s) => s.pages.length > 0),
  };
}

const RETAIL_TASKS: ReadonlyArray<ShellTask> = [
  {
    id: 'retail-users',
    title: 'Give someone access or change their role',
    blurb: 'People who can sign in, the roles they hold, and their operator initials.',
    path: `${RETAIL_BASE}/identity/users`,
    icon: 'user-check',
    crumb: 'Access and roles › Users',
    synonyms: ['add user', 'permissions', 'new employee'],
    tour: 'tile-identity-access',
  },
  {
    id: 'retail-history',
    title: 'See what changed, and undo a change',
    blurb: 'Who changed what, when, before and after, and why. Restore a change that was set wrong.',
    path: `${RETAIL_BASE}/history`,
    icon: 'history',
    crumb: 'Monitoring › Change history',
    synonyms: ['audit', 'restore', 'undo'],
    tour: 'tile-change-history',
  },
  {
    id: 'retail-recent-settings',
    title: 'Review recently changed settings',
    blurb: 'Every platform setting changed in the last 30 days, with its description and current value.',
    path: `${RETAIL_BASE}/system/settings?recent=1`,
    icon: 'list',
    crumb: 'Configuration › Settings',
  },
  {
    id: 'retail-rates',
    title: 'Change a deposit rate',
    blurb: 'The rates and yields the Rate and Fee Schedule prints, with the date each is valid as of.',
    path: `${RETAIL_BASE}/products/deposit-rates`,
    icon: 'chart',
    crumb: 'Configuration › Deposit rates',
    synonyms: ['apy', 'interest', 'truth in savings'],
  },
  {
    id: 'retail-products',
    title: 'Edit a product',
    blurb: 'The products customers can open, and whether each is offered.',
    path: `${RETAIL_BASE}/products/products`,
    icon: 'box',
    crumb: 'Configuration › Products',
  },
  {
    id: 'retail-holidays',
    title: 'Update the holiday calendar',
    blurb: 'The bank holidays every platform reads.',
    path: '/shared/holidays',
    icon: 'calendar',
    crumb: 'Shared › Holiday calendar',
    synonyms: ['holiday', 'closed'],
  },
  {
    id: 'retail-branches',
    title: 'Add or edit a branch',
    blurb: 'Branches and regions, kept by Retail and read by every platform.',
    path: '/shared/branches',
    icon: 'building',
    crumb: 'Shared › Branches',
  },
  {
    id: 'retail-templates',
    title: 'Change the wording of a notification',
    blurb: 'The text of the notifications the platform sends.',
    path: `${RETAIL_BASE}/notifications/templates`,
    icon: 'mail',
    crumb: 'Configuration › Notification templates',
    synonyms: ['email', 'message'],
  },
  {
    id: 'retail-bot-test-card',
    title: 'Check the Retail Chat Bot',
    blurb: 'Send yourself a test Teams card from the Retail Chat Bot. If it arrives, the bot works.',
    path: `${RETAIL_BASE}/tools/bot-test-card`,
    icon: 'tool',
    crumb: 'Tools › Send me a test Teams card',
    synonyms: ['test card', 'bot test', 'retail chat bot'],
  },
  {
    id: 'retail-core',
    title: 'Check the connection to Core',
    blurb: 'Diagnostics for the Core Director connection.',
    path: `${RETAIL_BASE}/system/jxchange-diagnostics`,
    icon: 'tool',
    crumb: 'Tools › jXchange diagnostics',
    synonyms: ['core down', 'jxchange'],
  },
];

export function retailTasksFor(roles: ReadonlyArray<string>): ShellTask[] {
  const visible = new Set(pagesFor(roles).map((pg) => pg.path));
  for (const it of sharedItemsFor(roles)) visible.add(`/shared/${it.slug}`);
  return RETAIL_TASKS.filter((t) => visible.has(t.path.split('?')[0] ?? t.path));
}

// ─── Loans ───────────────────────────────────────────────────────────────

export function loansNav(caps: Capabilities): ShellNav {
  const pages = loansPagesFor(caps.actions.map((a) => a.id));
  const enabled = new Set(caps.sections.map((s) => String(s.id)));
  return {
    root: { label: 'Loans', path: LOANS_BASE },
    homeHelp: LOANS_HOME_HELP,
    sections: SIX_SECTIONS.filter((s) => enabled.has(s.id)).map((s) => ({
      id: s.id,
      label: s.label,
      path: `${LOANS_BASE}/${s.id}`,
      holds: s.holds,
      pages: pages
        .filter((pg) => pg.section === s.id)
        .map<ShellPage>((pg) => ({ path: pg.path, label: pg.label, section: pg.section, group: pg.group, help: LOANS_GUIDE[pg.path]?.help, synonyms: LOANS_GUIDE[pg.path]?.synonyms })),
    })),
  };
}

export function loansTasksFor(caps: Capabilities): ShellTask[] {
  const actions = caps.actions.map((a) => a.id);
  const visible = new Set(loansPagesFor(actions).map((pg) => pg.path));
  return LOANS_TASKS.filter((t) => visible.has(t.path) && (t.id !== 'loans-job' || actions.includes('monitoring.jobs.mode')));
}

// ─── Innovation Hub ──────────────────────────────────────────────────────

export const HUB_BASE_PATH = '/hub';

export const HUB_HOME_HELP =
  "Hub settings, the admin audit and stuck card deliveries. Every write needs a reason and is recorded in the Hub's admin audit. The Hub dashboard keeps asks, support requests, backlog search, schedule and documents.";

/** Hub portal pages and the capability actions that list them (any one). */
const HUB_PAGES: ReadonlyArray<ShellPage & { actions: ReadonlyArray<string> }> = [
  {
    path: `${HUB_BASE_PATH}/configuration/settings`,
    label: 'Hub settings',
    section: 'configuration',
    actions: ['settings.list', 'settings.update'],
    help: "The Innovation Hub's settings. Every change needs a reason and is recorded in the Hub's admin audit.",
    synonyms: ['options', 'configuration'],
  },
  {
    path: `${HUB_BASE_PATH}/access/settings`,
    label: 'Access settings',
    section: 'access',
    actions: ['access.adminUpns', 'access.supportRoster'],
    help: 'Who administers the Hub and who is on the support roster.',
    synonyms: ['permissions'],
  },
  {
    path: `${HUB_BASE_PATH}/access/settings/hub.admin.upns`,
    label: 'Hub administrators',
    section: 'access',
    actions: ['access.adminUpns'],
    help: 'The exact signed-in accounts that are Hub administrators. An alias never counts, so an Azure Admin account must be listed itself.',
    synonyms: ['admins', 'administrator'],
  },
  {
    path: `${HUB_BASE_PATH}/access/settings/hub.support.roster`,
    label: 'Support roster',
    section: 'access',
    actions: ['access.supportRoster'],
    help: 'Who is on the Hub support roster.',
    synonyms: ['support', 'on call'],
  },
  {
    path: `${HUB_BASE_PATH}/monitoring/audit`,
    label: 'Admin audit',
    section: 'monitoring',
    actions: ['audit.list'],
    help: "The Hub's admin audit: each admin call, who made it, when, and the outcome.",
    synonyms: ['who changed', 'log', 'history'],
  },
  {
    path: `${HUB_BASE_PATH}/monitoring/deliveries`,
    label: 'Card deliveries',
    section: 'monitoring',
    actions: ['asks.deliveries'],
    help: 'Ask card deliveries by result, starting with the cards that failed to deliver.',
    synonyms: ['stuck card', 'failed card', 'teams card'],
  },
  {
    path: `${HUB_BASE_PATH}/tools/test-card`,
    label: 'Test ask card',
    section: 'tools',
    actions: ['tools.testCard', 'tools.testCard.list'],
    help: 'Sends a real test ask card from the Innovation Chat Bot to your own Teams. Claim, Release, Mark done and Decline work as on a real ask, but only the test changes: nothing else is written, nobody else is told, and the test never shows in any list. At most 3 open at a time; an unanswered one expires after 24 hours.',
    synonyms: ['test card', 'bot test', 'innovation chat bot', 'check the bot'],
  },
];

/** The Hub portal pages this admin may open: a registry page is listed when one of its actions is in the capability
 * answer. A capability action with no page in the portal is not listed anywhere. */
export function hubPagesFor(caps: Capabilities): ShellPage[] {
  const have = new Set(caps.actions.map((a) => a.id));
  return HUB_PAGES.filter((pg) => pg.actions.some((a) => have.has(a))).map(({ actions: _a, ...pg }) => pg);
}

export function hubNav(caps: Capabilities): ShellNav {
  const pages = hubPagesFor(caps);
  const enabled = new Set(caps.sections.map((s) => String(s.id)));
  return {
    root: { label: caps.displayName ?? 'Innovation Hub', path: HUB_BASE_PATH },
    homeHelp: HUB_HOME_HELP,
    sections: SIX_SECTIONS.filter((s) => enabled.has(s.id)).map((s) => ({
      id: s.id,
      label: s.label,
      path: `${HUB_BASE_PATH}/${s.id}`,
      holds: s.holds,
      pages: pages.filter((pg) => pg.section === s.id),
    })),
  };
}

const HUB_TASKS: ReadonlyArray<ShellTask & { action: string }> = [
  {
    id: 'hub-admins',
    action: 'access.adminUpns',
    title: 'Change who is a Hub administrator',
    blurb: 'Add or remove an account on the Hub administrator list.',
    path: `${HUB_BASE_PATH}/access/settings/hub.admin.upns`,
    icon: 'key',
    crumb: 'Access and roles › Hub administrators',
  },
  {
    id: 'hub-roster',
    action: 'access.supportRoster',
    title: 'Change the support roster',
    blurb: 'Who is on the Hub support roster.',
    path: `${HUB_BASE_PATH}/access/settings/hub.support.roster`,
    icon: 'users',
    crumb: 'Access and roles › Support roster',
  },
  {
    id: 'hub-settings',
    action: 'settings.list',
    title: 'Change a Hub setting',
    blurb: 'Every change needs a reason and is recorded in the admin audit.',
    path: `${HUB_BASE_PATH}/configuration/settings`,
    icon: 'sliders',
    crumb: 'Configuration › Hub settings',
  },
  {
    id: 'hub-deliveries',
    action: 'asks.deliveries',
    title: 'Find a card that did not arrive',
    blurb: 'Ask cards that failed to deliver, and where they were going.',
    path: `${HUB_BASE_PATH}/monitoring/deliveries`,
    icon: 'mail',
    crumb: 'Monitoring › Card deliveries',
  },
  {
    id: 'hub-test-card',
    action: 'tools.testCard',
    title: 'Check the Innovation Chat Bot',
    blurb: 'Send yourself a test ask card and press it in Teams. Nothing real is decided and nobody else is told.',
    path: `${HUB_BASE_PATH}/tools/test-card`,
    icon: 'tool',
    crumb: 'Tools › Test ask card',
    synonyms: ['test card', 'bot test', 'teams card'],
  },
  {
    id: 'hub-audit',
    action: 'audit.list',
    title: 'See who changed what in the Hub',
    blurb: 'The admin audit: each call, who made it, when, and the outcome.',
    path: `${HUB_BASE_PATH}/monitoring/audit`,
    icon: 'history',
    crumb: 'Monitoring › Admin audit',
  },
];

export function hubTasksFor(caps: Capabilities): ShellTask[] {
  const have = new Set(caps.actions.map((a) => a.id));
  return HUB_TASKS.filter((t) => have.has(t.action)).map(({ action: _a, ...t }) => t);
}

// ─── Shared ──────────────────────────────────────────────────────────────

const SHARED_HELP: Readonly<Record<string, { help: string; synonyms?: ReadonlyArray<string> }>> = {
  holidays: { help: "The bank's holiday calendar. Retail keeps it; every platform reads it.", synonyms: ['holiday', 'closed', 'calendar'] },
  branches: { help: 'Every branch: add, edit or retire one. Retail keeps the list; every platform reads it.', synonyms: ['branch', 'location', 'office'] },
  regions: { help: 'The regions branches belong to. Retail keeps the list; every platform reads it.', synonyms: ['region', 'area'] },
};

export function sharedNav(roles: ReadonlyArray<string>): ShellNav {
  return {
    root: { label: 'Shared', path: '/shared' },
    hideHome: true,
    sections: [
      {
        id: 'shared',
        label: 'Reference data',
        holds: 'Bank-wide lists kept by Retail Operations and read by every platform.',
        pages: sharedItemsFor(roles).map<ShellPage>((it) => ({
          path: `/shared/${it.slug}`,
          label: it.label,
          section: 'shared',
          help: SHARED_HELP[it.slug]?.help,
          synonyms: SHARED_HELP[it.slug]?.synonyms,
        })),
      },
    ],
  };
}

// ─── The hook the shell uses ─────────────────────────────────────────────

const ready = (s: PlatformStatus): Capabilities | null => (s.kind === 'ready' ? s.caps : null);

export function platformOfPath(pathname: string): AdminPlatformId | null {
  const seg = pathname.split('/').filter(Boolean)[0] ?? '';
  return seg === 'retail' || seg === 'loans' || seg === 'hub' || seg === 'shared' ? seg : null;
}

export interface AdminShellModel {
  platforms: ShellPlatform[];
  currentId: AdminPlatformId | null;
  nav: ShellNav | null;
  searchItems: SearchItem[];
  apiVersion: string | null;
}

export function useAdminShell(pathname: string): AdminShellModel {
  const { user } = useAuth();
  const { status, available } = usePlatforms();
  const roles = useMemo(() => user?.roles ?? [], [user]);

  const navs = useMemo(() => {
    const out: Partial<Record<AdminPlatformId, ShellNav>> = {};
    const retail = ready(status.retail);
    const loans = ready(status.loans);
    const hub = ready(status.hub);
    if (retail) out.retail = retailNav(roles);
    if (loans) out.loans = loansNav(loans);
    if (hub) out.hub = hubNav(hub);
    if (retail) out.shared = sharedNav(roles);
    return out;
  }, [status, roles]);

  const searchItems = useMemo(() => {
    const items: SearchItem[] = [];
    const retail = ready(status.retail);
    const loans = ready(status.loans);
    const hub = ready(status.hub);
    for (const nav of Object.values(navs) as ShellNav[]) items.push(...pagesToSearchItems(nav, nav.root.label));
    if (retail) items.push(...tasksToSearchItems(retailTasksFor(roles), 'Retail'));
    if (loans) items.push(...tasksToSearchItems(loansTasksFor(loans), 'Loans'));
    if (hub) items.push(...tasksToSearchItems(hubTasksFor(hub), hub.displayName ?? 'Innovation Hub'));
    return items;
  }, [navs, status, roles]);

  const platforms = useMemo<ShellPlatform[]>(() => {
    const list: ShellPlatform[] = available.filter((p) => p.id !== 'finance').map((p) => ({ id: p.id, label: p.label, path: p.path }));
    if (status.retail.kind === 'ready') list.push({ id: 'shared', label: 'Shared', path: '/shared' });
    return list;
  }, [available, status.retail.kind]);

  const currentId = platformOfPath(pathname);
  const nav = currentId ? navs[currentId] ?? null : null;
  const caps = currentId === 'retail' ? ready(status.retail) : currentId === 'loans' ? ready(status.loans) : currentId === 'hub' ? ready(status.hub) : null;
  const apiName = currentId === 'retail' ? 'pcb-retail-api' : currentId === 'loans' ? 'pcb-loans-api' : currentId === 'hub' ? 'pcb-innovation-api' : null;
  const apiVersion = caps?.apiVersion && apiName ? `${apiName} ${caps.apiVersion}` : null;

  return { platforms, currentId, nav, searchItems, apiVersion };
}
